import {
  APP_INITIALIZER,
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import { ConfirmationService, MessageService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

import { routes } from './app.routes';
import { GlobalSettingsService } from '@c3library/services/global-settings.service';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '@environments/environment';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { AuthTokenInterceptor } from '@c3library/services/auth/auth.interceptor';

// ---------- THEMES ----------
const MyTheme = definePreset(Aura, {
  options: { darkModeSelector: false },
  primitive: {
    surface: {
      0: '#ffffff',
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },
  semantic: {
    primary: {
      50: 'color-mix(in srgb, var(--app-primary-color) 50%, #fff)',
      100: 'color-mix(in srgb, var(--app-primary-color) 60%, #fff)',
      200: 'color-mix(in srgb, var(--app-primary-color) 70%, #fff)',
      300: 'color-mix(in srgb, var(--app-primary-color) 80%, #fff)',
      400: 'color-mix(in srgb, var(--app-primary-color) 90%, #fff)',
      500: 'var(--app-primary-color)',
      600: 'color-mix(in srgb, var(--app-primary-color) 90%, #000)',
      700: 'color-mix(in srgb, var(--app-primary-color) 80%, #000)',
      800: 'color-mix(in srgb, var(--app-primary-color) 70%, #000)',
      900: 'color-mix(in srgb, var(--app-primary-color) 60%, #000)',
    },
  },
});

function provideTheme(config: PrimeNG) {
  return () => {
    config.theme.set({ preset: MyTheme, options: { darkModeSelector: false } });
  };
}

export function msalInstanceFactory() {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureADClientId,
      authority: environment.azureADAuthority,
      redirectUri: environment.azureADRedirectUri,
    },
    cache: { cacheLocation: 'localStorage', storeAuthStateInCookie: false },
  });
}

export function msalInterceptorConfigFactory() {
  const protectedResourceMap = new Map<string, string[]>();
  //protectedResourceMap.set('https://localhost:7047/', [environment.apiScope]);
  return { interactionType: InteractionType.Redirect, protectedResourceMap };
}

export function msalInitializer(msalService: MsalService) {
  return () => msalService.instance.initialize();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
    ),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideCharts(withDefaultRegisterables()),
    provideAnimationsAsync(),

    MessageService,
    ConfirmationService,
    GlobalSettingsService,
    providePrimeNG({ theme: { preset: MyTheme, options: { darkModeSelector: false } } }),
    { provide: APP_INITIALIZER, useFactory: provideTheme, deps: [PrimeNG], multi: true },

    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: APP_INITIALIZER, useFactory: msalInitializer, deps: [MsalService], multi: true },
  ],
};
